import type { Field } from '@/modules/Project/Fields/types'
import { exhaustiveGuard, invariant } from '@/shared/utils/typeAssertions'
import type { Entity } from '../useProject'
import { matchSelectValue } from './fieldMaybePassesFilters'
import type {
  FieldNameFilter,
  FilenameFilter,
  Filter,
  MatcherName,
  NumberValueFilter,
  StatusFilter,
} from './types'
import { getFilterComparisonValue } from './utils'

/**
 * Returns a boolean value to indicate whether an entity passes the given filter. Usually,
 * filtering is done on the backend. But there are situations where we need to run the logic on
 * the frontend.
 *
 * e.g. when we receive a 'field:updated' websocket event, we need to check if the entity should
 * be removed from the table with its new field value.
 */
export const entityPassesFilters = (entity: Entity, filter: Filter | undefined): boolean => {
  if (!filter) {
    return true
  }

  if ('matcher' in filter) {
    if (filter.subject === 'active_view_id') {
      return filterByActiveView(entity, filter.matcher.name, filter.matcher.values)
    }

    if (
      filter.subject === 'select_option_value' ||
      filter.subject === 'field_select_option_value'
    ) {
      return filterBySelectOptionValue(entity, {
        matcherName: filter.matcher.name,
        propertyId: filter.matcher.property_id,
        values: filter.matcher.values,
        caseSensitive:
          filter.matcher.name === 'property_all_of' ? false : filter.matcher.case_sensitive,
      })
    }

    if (filter.subject === 'parent_entity_id') {
      return filterByParentId(entity, {
        matcherName: filter.matcher.name,
        values: filter.matcher.values,
      })
    }

    if (filter.subject === 'status' || filter.subject === 'field_status') {
      return filterByStatus(entity, filter.matcher.property_id, filter.matcher.values)
    }

    if (filter.subject === 'id' || filter.subject === 'entity_id') {
      return filterById(entity, filter.matcher.name, filter.matcher.values)
    }

    if (filter.subject === 'text_value' || filter.subject === 'field_text_value') {
      const field = entity.fields.get(filter.matcher.property_id)
      if (!field) {
        return false
      }
      return filterByTextValue(field, {
        matcherName: filter.matcher.name,
        values: filter.matcher.values,
        caseSensitive: filter.matcher.case_sensitive,
      })
    }

    if (filter.subject === 'field_file_name') {
      const field = entity.fields.get(filter.matcher.property_id)
      invariant(field && 'manualFilename' in field, 'Expected field to be a file field')

      return filterbyFilename(field, filter.matcher)
    }

    if (filter.subject === 'field_metadata_field_name') {
      const field = entity.fields.get(filter.matcher.property_id)
      invariant(field && 'manualName' in field, 'Field must have a manualName property')

      return filterByFieldName(field, filter.matcher)
    }

    if (filter.subject === 'entity_status') {
      return filterByEntityStatus(entity, filter.matcher)
    }

    if (filter.subject === 'field_value_semantics') {
      throw new Error('TODO: implement semantic filter')
    }

    if (filter.subject === 'field_number_unit') {
      // Not yet implemented
      return true
    }

    if (filter.subject === 'field_number_value') {
      const field = entity.fields.get(filter.matcher.property_id)
      invariant(
        field?.type === 'number',
        'Number value filter can only be applied to number fields',
      )
      return filterByNumberValue(field, filter.matcher)
    }

    // If TS is complaining in this function, you might need to handle another subject here.
    // Don't just return false, as that would mask the error and not apply the filter.
    exhaustiveGuard(filter, 'Unhandled filter subject')
  }

  if (filter.conjunction === 'or') {
    return filter.filters.some((f) => entityPassesFilters(entity, f))
  }

  return filter.filters.every((f) => entityPassesFilters(entity, f))
}

const filterByActiveView = (
  entity: Entity,
  matcher: Extract<MatcherName, 'any_of' | 'none_of'>,
  viewIds: string[],
): boolean => {
  if (!entity.activeViewIds) {
    return false
  }

  if (matcher === 'any_of') {
    return entity.activeViewIds.some((id) => viewIds.includes(id)) ?? false
  }

  return !entity.activeViewIds.some((id) => viewIds.includes(id))
}

const filterBySelectOptionValue = (
  entity: Entity,
  {
    matcherName,
    propertyId,
    values,
    caseSensitive = true,
  }: {
    matcherName: Extract<MatcherName, 'property_any_of' | 'property_none_of' | 'property_all_of'>
    propertyId: string
    values: string[]
    caseSensitive?: boolean
  },
): boolean => {
  const field = entity.fields.get(propertyId)
  if (!field) {
    return false
  }

  return matchSelectValue(field, { matcherName, values, caseSensitive })
}

const filterByParentId = (
  entity: Entity,
  {
    matcherName,
    values,
  }: { matcherName: Extract<MatcherName, 'any_of' | 'none_of'>; values: string[] },
): boolean => {
  if (matcherName === 'any_of') {
    return !!entity.parentEntityId && values.includes(entity.parentEntityId)
  }

  return !entity.parentEntityId || !values.includes(entity.parentEntityId)
}

const filterByStatus = (entity: Entity, propertyId: string, values: string[]) => {
  const field = entity.fields.get(propertyId)
  if (!field) {
    return false
  }

  return values.includes(field.status)
}

const filterById = (
  entity: Entity,
  matcherName: Extract<MatcherName, 'any_of' | 'none_of'>,
  values: string[],
) => {
  if (matcherName === 'any_of') {
    return values.includes(entity.id)
  }

  return !values.includes(entity.id)
}

export const filterByTextValue = (
  field: Field,
  {
    caseSensitive = true,
    matcherName,
    values: _values,
  }: {
    matcherName:
      | 'property_any_of'
      | 'property_none_of'
      | 'property_contains_any_of'
      | 'property_contains_none_of'
    values: string[]
    caseSensitive?: boolean
  },
): boolean => {
  if (field.type !== 'text') {
    return false
  }

  const manualValue = getFilterComparisonValue(field.manualValue, caseSensitive)
  const toolValue = getFilterComparisonValue(field.toolValue, caseSensitive)
  const values = _values.map((v) => (caseSensitive ? v : v.toLowerCase()))

  if (matcherName === 'property_any_of') {
    return values.some((v) => manualValue === v || toolValue === v)
  }

  if (matcherName === 'property_none_of') {
    return !values.some((v) => manualValue === v || toolValue === v)
  }

  if (matcherName === 'property_contains_any_of') {
    return values.some((v) => manualValue?.includes(v) || toolValue?.includes(v))
  }

  if (matcherName === 'property_contains_none_of') {
    return !values.some((v) => manualValue?.includes(v) || toolValue?.includes(v))
  }

  return false
}

/**
 * Filters a file-like field by filename.
 * @param field
 * @param matcher
 * @returns
 */
export function filterbyFilename(
  field: Extract<Field, { manualFilename: Field<'file'>['manualFilename'] }>,
  matcher: FilenameFilter['matcher'],
): boolean {
  const manualFilename = matcher.case_sensitive
    ? field.manualFilename
    : field.manualFilename?.toLowerCase()
  const toolFilename = matcher.case_sensitive
    ? field.toolFilename
    : field.toolFilename?.toLowerCase()
  const normalizedValues = matcher.values.map((v) => (matcher.case_sensitive ? v : v.toLowerCase()))

  if (matcher.name === 'property_any_of') {
    if (manualFilename) {
      return normalizedValues.some((v) => manualFilename.includes(v))
    }

    return normalizedValues.some((v) => toolFilename?.includes(v))
  }

  if (matcher.name === 'property_none_of') {
    if (manualFilename) {
      return !normalizedValues.some((v) => manualFilename.includes(v))
    }

    return !normalizedValues.some((v) => toolFilename?.includes(v))
  }

  if (matcher.name === 'property_contains_any_of') {
    if (manualFilename) {
      return normalizedValues.some((v) => manualFilename.includes(v))
    }

    return normalizedValues.some((v) => toolFilename?.includes(v))
  }

  if (matcher.name === 'property_contains_none_of') {
    if (manualFilename) {
      return !normalizedValues.some((v) => manualFilename.includes(v))
    }

    return !normalizedValues.some((v) => toolFilename?.includes(v))
  }

  exhaustiveGuard(matcher, 'Unhandled matcher name')
  return false
}

export function filterByFieldName(
  field: Extract<Field, { manualName: Field<'collection'>['manualName'] }>,
  matcher: FieldNameFilter['matcher'],
): boolean {
  const manualName = matcher.case_sensitive ? field.manualName : field.manualName?.toLowerCase()
  const normalizedValues = matcher.values.map((v) => (matcher.case_sensitive ? v : v.toLowerCase()))

  if (matcher.name === 'property_any_of') {
    return normalizedValues.some((v) => manualName === v)
  }

  if (matcher.name === 'property_none_of') {
    return !normalizedValues.some((v) => manualName === v)
  }

  if (matcher.name === 'property_contains_any_of') {
    return !!manualName && normalizedValues.some((v) => manualName.includes(v))
  }

  if (matcher.name === 'property_contains_none_of') {
    return !manualName || !normalizedValues.some((v) => manualName.includes(v))
  }

  exhaustiveGuard(matcher, 'Unhandled matcher name')
  return false
}

export function filterByEntityStatus(entity: Entity, matcher: StatusFilter['matcher']): boolean {
  const filterValue = matcher.values[0]
  const fields = [...entity.fields.values()]

  switch (filterValue) {
    case 'computing':
    case 'error':
    case 'idle': {
      return fields.some((field) => field.status === filterValue)
    }
    case 'complete': {
      return fields.every((field) => field.status === 'complete')
    }
    default: {
      return false
    }
  }
}

/**
 * Filters a number field by a number value filter.
 */
export function filterByNumberValue(
  field: Field<'number'>,
  matcher: NumberValueFilter['matcher'],
): boolean {
  /**
   * Note that we may lose some precision for very large numbers when converting the number field
   * values to number. This is probably fine for here, because this function is only used to decide
   * whether to show/hide an entity in response to websocket messages. The alternative would be
   * to go all in on backend filtering, and reload the table every time we get a websocket message
   * - not a particularly great UX.
   */
  const manual = field.manualValue === null ? null : Number(field.manualValue)
  const tool = field.toolValue === null ? null : Number(field.toolValue)
  const valueAsNumber = manual ?? tool

  switch (matcher.name) {
    case 'property_any_of': {
      const manualValueMatches =
        field.manualValue !== null && matcher.values.includes(field.manualValue)
      const toolValueMatches = field.toolValue !== null && matcher.values.includes(field.toolValue)
      return manualValueMatches || toolValueMatches
    }
    case 'property_none_of': {
      const manualValueMatches =
        field.manualValue !== null && matcher.values.includes(field.manualValue)
      const toolValueMatches = field.toolValue !== null && matcher.values.includes(field.toolValue)
      return !manualValueMatches && !toolValueMatches
    }
    case 'property_greater_than': {
      const searchAsNumber = Number(matcher.value)
      return !!valueAsNumber && valueAsNumber > searchAsNumber
    }
    case 'property_greater_than_or_equal_to': {
      const searchAsNumber = Number(matcher.value)
      return !!valueAsNumber && valueAsNumber >= searchAsNumber
    }
    case 'property_less_than': {
      const searchAsNumber = Number(matcher.value)
      return !!valueAsNumber && valueAsNumber < searchAsNumber
    }
    case 'property_less_than_or_equal_to': {
      const searchAsNumber = Number(matcher.value)
      return !!valueAsNumber && valueAsNumber <= searchAsNumber
    }
    default: {
      return exhaustiveGuard(matcher, 'Unhandled matcher name')
    }
  }
}
